import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import type { TLAIApiError } from '@/types/api';
import { safeInvoke } from '@/utils/safe-invoke';

type StreamHookState<TItem> = {
  items: TItem[];
  error: TLAIApiError | null;
  isStreaming: boolean;
  itemsRef: RefObject<TItem[]>;
  reset: () => void;
};

type StreamHook<TRequest, TItem> = [
  /**
   * @description Initializes the stream.
   * @param request The request that will be used to initialize the stream.
   * @returns A promise that resolves to the error that occurred, if any.
   */
  init: (request: TRequest) => Promise<TLAIApiError | null>,
  state: StreamHookState<TItem>,
];

type StreamHookOptions<TRequest, TItem> = {
  /**
   * @description Called when a new item is received from the stream.
   * @param item The item that was received.
   * @param items The items that have been received so far.
   * @param request The request that was used to initialize the stream.
   */
  onReceive?: (item: TItem, items: TItem[], request: TRequest) => Promise<void> | void;
  /**
   * @description Called when the stream is complete.
   * @param items The items that were received from the stream.
   * @param request The request that was used to initialize the stream.
   */
  onComplete?: (items: TItem[], request: TRequest) => Promise<void> | void;
};

export const useServerFnStream = <TRequest, TItem>(
  serverFn: (input: { data: TRequest }) => Promise<AsyncIterable<TItem>>,
  options?: StreamHookOptions<TRequest, TItem>,
): StreamHook<TRequest, TItem> => {
  const runIdRef = useRef(0);
  const itemsRef = useRef<TItem[]>([]);
  const [items, setItems] = useState<TItem[]>([]);
  const [error, setError] = useState<TLAIApiError | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const reset = useCallback(() => {
    runIdRef.current += 1;
    itemsRef.current = [];
    setItems([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    return () => {
      runIdRef.current += 1;
    };
  }, []);

  const init = useCallback(
    async (request: TRequest) => {
      let resolvedError: TLAIApiError | null = null;
      const runId = (runIdRef.current += 1);
      setIsStreaming(true);
      setError(null);
      setItems([]);
      itemsRef.current = [];

      const streamedItems: TItem[] = [];

      await safeInvoke(
        async () => {
          const stream = await serverFn({ data: request });

          for await (const item of stream) {
            if (runIdRef.current !== runId) break;
            streamedItems.push(item);
            itemsRef.current = streamedItems;
            setItems((prev) => [...prev, item]);
            options?.onReceive?.(item, streamedItems, request);
          }
        },
        {
          onError: (err) => {
            if (runIdRef.current === runId) {
              resolvedError = err as unknown as TLAIApiError;
              setError(resolvedError);
            }
          },
          onFinally: () => {
            if (runIdRef.current === runId) {
              setIsStreaming(false);
            }
          },
        },
      );

      if (runIdRef.current === runId) {
        await options?.onComplete?.(streamedItems, request);
      }

      return resolvedError;
    },
    [options, serverFn],
  );

  return [init, { items, error, isStreaming, itemsRef, reset }];
};
